<template>
  <SideMenu
    ref="sideMenu"
    :heading="activeTab === 'reviews' ? $lang('product_page', 'reviews') : $lang('product_page', 'faq')"
    :side="'right'"
    @on-side-menu-close="$emit('onProductReviewClose')"
  >
    <div class="bg-main h-full flex flex-col">
      <!-- Tab Navigation -->
      <div class="border-b border-border">
        <div class="flex px-16 pt-16">
          <button
            class="flex-1 py-12 px-12 text-center text-sm font-medium transition-all border-b-2"
            :class="activeTab === 'reviews' ? 'border-black text-black' : 'border-transparent text-gray-500 hover:text-black'"
            @click="activeTab = 'reviews'"
          >
            {{ (bottomline?.total_reviews || totalReviews || 0) > 1 ? $lang('product_page', 'reviews') : $lang('reviews', 'review') }} ({{ bottomline?.total_reviews || totalReviews || 0 }})
          </button>
          <button
            class="flex-1 py-12 px-12 text-center text-sm font-medium transition-all border-b-2"
            :class="activeTab === 'qa' ? 'border-black text-black' : 'border-transparent text-gray-500 hover:text-black'"
            @click="activeTab = 'qa'"
          >
            {{ $lang('product_page', 'faq') }} ({{ totalQuestions }})
          </button>
        </div>
      </div>

      <!-- Content Area -->
      <div ref="scrollContainer" class="md:px-24 px-16 overflow-y-auto flex-1 pb-80">
        <!-- Reviews Tab -->
        <div v-show="activeTab === 'reviews'" class="py-16">
          <!-- Summary Section -->
          <div v-if="bottomline && bottomline.total_reviews > 0" class="mb-24 pb-24 border-b border-border">
            <div class="flex items-center gap-12 mb-12">
              <div class="text-4xl font-bold">{{ bottomline.average_score?.toFixed(1) || '0.0' }}</div>
              <div>
                <div class="flex gap-4 mb-4">
                  <fa
                    v-for="star in 5"
                    :key="star"
                    :icon="getStarIcon(star, bottomline.average_score || 0)"
                    :style="getStarStyle(star, bottomline.average_score || 0)"
                    class="text-lg"
                  />
                </div>
                <div class="text-base text-gray-600">{{ bottomline.total_reviews }} {{ bottomline.total_reviews > 1 ? $lang('product_page', 'reviews') : $lang('reviews', 'review') }}</div>
              </div>
            </div>

            <!-- Product Fit Summary -->
            <div v-if="hasSizeFitData" class="mt-16 pt-16 border-t border-border">
              <div class="text-xs font-medium text-gray-600 mb-8">{{ $lang('reviews', 'size_fit_label') }}</div>
              <div class="flex gap-8">
                <div 
                  v-if="sizeFitPercentages.small > 0"
                  class="flex-1 text-center py-8 px-12 rounded-regular text-xs"
                  style="background-color: #fef3c7; color: #92400e;"
                >
                  <div class="font-medium">{{ sizeFitPercentages.small }}%</div>
                  <div class="text-2xs mt-2">{{ $lang('reviews', 'runs_small') }}</div>
                </div>
                <div 
                  v-if="sizeFitPercentages.as_expected > 0"
                  class="flex-1 text-center py-8 px-12 rounded-regular text-xs"
                  style="background-color: #dcfce7; color: #16a34a;"
                >
                  <div class="font-medium">{{ sizeFitPercentages.as_expected }}%</div>
                  <div class="text-2xs mt-2">{{ $lang('reviews', 'true_to_size') }}</div>
                </div>
                <div 
                  v-if="sizeFitPercentages.large > 0"
                  class="flex-1 text-center py-8 px-12 rounded-regular text-xs"
                  style="background-color: #dbeafe; color: #1e40af;"
                >
                  <div class="font-medium">{{ sizeFitPercentages.large }}%</div>
                  <div class="text-2xs mt-2">{{ $lang('reviews', 'runs_large') }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Write Review Button -->
          <div v-if="!showReviewForm" class="mb-24">
            <ButtonComponent
              :text="$lang('reviews', 'write_review')"
              is-block
              @clicked="showReviewForm = true"
            />
          </div>

          <!-- Review Form -->
          <ReviewForm
            v-if="showReviewForm"
            :product="product"
            @review-submitted="onReviewSubmitted"
            @cancel="showReviewForm = false"
          />

          <!-- Reviews List -->
          <ReviewList
            v-if="!showReviewForm"
            :reviews="reviews"
            :total-reviews="totalReviews"
            :loading="loadingReviews"
            :show-filters="true"
            :show-votes="false"
            @filter-change="onFilterChange"
          />

          <!-- Load More -->
          <div v-if="hasMoreReviews && !loadingReviews && !showReviewForm" class="mt-24">
            <ButtonComponent
              :text="$lang('reviews', 'load_more')"
              is-secondary-btn
              is-block
              @clicked="loadMoreReviews"
            />
          </div>

          <!-- Loading State -->
          <div v-if="loadingReviews" class="flex justify-center py-24">
            <fa :icon="['fal', 'spinner']" :spin="true" class="text-2xl text-muted" />
          </div>
        </div>

        <!-- Q&A Tab -->
        <div v-show="activeTab === 'qa'" class="py-16">
          <ProductQA :product="product" @questions-loaded="onQuestionsLoaded" />
        </div>
      </div>
    </div>
  </SideMenu>
</template>

<script setup lang="ts">
import { defineAsyncComponent, nextTick } from 'vue';
import SideMenu from '~/components/navigation/SideMenu.vue';
import ButtonComponent from '~/components/forms/ButtonComponent.vue';
import type Product from '~/models/product';

// Lazy load the review components
const ReviewForm = defineAsyncComponent({
  loader: () => import('~/components/productpage/ReviewForm.vue'),
  errorComponent: { template: '<div class="text-red-500 p-24">Error loading review form</div>' },
  loadingComponent: { template: '<div class="p-24">Loading form...</div>' },
  delay: 200,
  timeout: 5000,
  onError(error, retry, fail) {
    console.error('[ProductReview] Error loading ReviewForm:', error);
    fail();
  }
});

const ReviewList = defineAsyncComponent({
  loader: () => import('~/components/productpage/ReviewList.vue'),
  errorComponent: { template: '<div class="text-red-500 p-24">Error loading review list</div>' },
  onError(error) {
    console.error('[ProductReview] Error loading ReviewList:', error);
  }
});

const ProductQA = defineAsyncComponent({
  loader: () => import('~/components/productpage/ProductQA.vue'),
  errorComponent: { template: '<div class="text-red-500 p-24">Error loading Q&A</div>' },
  onError(error) {
    console.error('[ProductReview] Error loading ProductQA:', error);
  }
});

const props = withDefaults(defineProps<{
  product: Product,
  initialTab?: 'reviews' | 'qa',
}>(), {
  initialTab: 'reviews',
});

defineEmits<{
  (e: 'onProductReviewClose'): void;
}>();

const { fetchReviews, fetchBottomline, fetchAllReviews } = useYotpo();

const sideMenu = ref<typeof SideMenu | null>(null);
const scrollContainer = ref<HTMLDivElement | null>(null);
const activeTab = ref<'reviews' | 'qa'>(props.initialTab);
const showReviewForm = ref(false);
const reviews = ref<any[]>([]);
const allReviewsForSizeFit = ref<any[]>([]); // ALL reviews for accurate size fit calculation
const totalReviews = ref(0);
const totalQuestions = ref(0);
const loadingReviews = ref(false);
const currentPage = ref(1);
const currentFilter = ref<any>({});
const bottomline = ref<any>(null);
const hasMoreToLoad = ref(true);

// Watch for tab changes and scroll to top
watch(activeTab, () => {
  if (scrollContainer.value) {
    scrollContainer.value.scrollTop = 0;
  }
});

// Watch for showReviewForm changes
watch(showReviewForm, (newVal: boolean) => {
  // Form state changed
});

const hasMoreReviews = computed(() => {
  const perPage = 10;
  return hasMoreToLoad.value && totalReviews.value > reviews.value.length;
});

// Calculate size fit percentages from bottomline OR from reviews
const sizeFitPercentages = computed(() => {
  // Try to get from bottomline first (field ID --93155)
  const sizeField = bottomline.value?.custom_fields_bottomline?.['--93155'];
  
  // If bottomline HAS aggregated data, use it (most efficient)
  if (sizeField && Object.keys(sizeField).length > 0) {
    const counts = {
      1: parseInt(sizeField['1'] || '0'),
      2: parseInt(sizeField['2'] || '0'),
      3: parseInt(sizeField['3'] || '0'),
    };
    
    const total = counts[1] + counts[2] + counts[3];
    
    if (total === 0) {
      return { small: 0, as_expected: 0, large: 0 };
    }
    
    return {
      small: Math.round((counts[1] / total) * 100),
      as_expected: Math.round((counts[2] / total) * 100),
      large: Math.round((counts[3] / total) * 100),
    };
  }
  
  // Fallback: calculate from all loaded reviews if available
  if (allReviewsForSizeFit.value.length > 0) {
    const counts = {
      1: 0, // smaller than expected
      2: 0, // as expected
      3: 0  // larger than expected
    };
    
    allReviewsForSizeFit.value.forEach(review => {
      const sizeFieldValue = review.custom_fields?.['--93155']?.value;
      if (sizeFieldValue && (sizeFieldValue === 1 || sizeFieldValue === 2 || sizeFieldValue === 3)) {
        counts[sizeFieldValue]++;
      }
    });
    
    const total = counts[1] + counts[2] + counts[3];
    
    if (total === 0) {
      return { small: 0, as_expected: 0, large: 0 };
    }
    
    const percentages = {
      small: Math.round((counts[1] / total) * 100),
      as_expected: Math.round((counts[2] / total) * 100),
      large: Math.round((counts[3] / total) * 100),
    };
    return percentages;
  }
  
  // No data available yet
  return { small: 0, as_expected: 0, large: 0 };
});

const hasSizeFitData = computed(() => {
  const percentages = sizeFitPercentages.value;
  return percentages.small > 0 || percentages.as_expected > 0 || percentages.large > 0;
});

// Helper function to get star icon type
const getStarIcon = (starPosition: number, score: number): [string, string] => {
  // Round to nearest 0.5
  const roundedScore = Math.round(score * 2) / 2;
  
  if (starPosition <= Math.floor(roundedScore)) {
    // Full star
    return ['fas', 'star'];
  } else if (starPosition === Math.ceil(roundedScore) && roundedScore % 1 !== 0) {
    // Half star
    return ['fas', 'star-half-alt'];
  } else {
    // Empty star
    return ['far', 'star'];
  }
};

// Helper function to get star color style
const getStarStyle = (starPosition: number, score: number): { color: string } => {
  // Round to nearest 0.5
  const roundedScore = Math.round(score * 2) / 2;
  
  // Yellow for full and half stars, gray for empty
  if (starPosition <= Math.ceil(roundedScore)) {
    return { color: '#fbbf24' }; // yellow-400
  }
  return { color: '#d1d5db' }; // gray-300
};

onMounted(async () => {
  if (sideMenu.value) {
    sideMenu.value.openOverlay();
  }
  
  // Load reviews and bottomline first
  await Promise.all([
    loadReviews(),
    loadBottomline(),
  ]);
  
  // Then conditionally load all reviews for size fit if needed
  await loadAllReviewsForSizeFit();
});

const loadReviews = async (reset = false) => {
  if (reset) {
    currentPage.value = 1;
    reviews.value = [];
    hasMoreToLoad.value = true;
  }

  loadingReviews.value = true;
  try {
    const result = await fetchReviews(props.product.sku, {
      page: currentPage.value,
      per_page: 10,
      ...currentFilter.value
    });

    if (result && result.success) {
      // Handle both response structures: data.response or data directly
      const responseData = result.data.response || result.data;
      const reviewsData = responseData.reviews || [];
      const paginationData = responseData.pagination || { total: reviewsData.length, page: 1, per_page: 10 };

      if (reset) {
        reviews.value = reviewsData;
      } else {
        reviews.value = [...reviews.value, ...reviewsData];
      }
      totalReviews.value = paginationData.total || reviewsData.length;
      
      // Check if there are more reviews to load
      // If we received fewer reviews than per_page, we've reached the end
      hasMoreToLoad.value = reviewsData.length >= 10;
    }
  } catch (error) {
    console.error('Failed to load reviews:', error);
  } finally {
    loadingReviews.value = false;
  }
};

const loadBottomline = async () => {
  try {
    const result = await fetchBottomline(props.product.sku);
    if (result && result.success) {
      // Handle both response structures
      const responseData = result.data.response || result.data;
      bottomline.value = responseData.bottomline || { average_score: 0, total_reviews: 0 };
      
      // Update totalReviews from bottomline
      if (bottomline.value.total_reviews) {
        totalReviews.value = bottomline.value.total_reviews;
      }
    }
  } catch (error) {
    console.error('Failed to load bottomline:', error);
  }
};

// Load ALL reviews for accurate size fit calculation (not paginated)
// Only loads if bottomline doesn't have aggregated data
const loadAllReviewsForSizeFit = async () => {
  // Check if bottomline already has aggregated size fit data
  const sizeField = bottomline.value?.custom_fields_bottomline?.['--93155'];
  if (sizeField && Object.keys(sizeField).length > 0) {
    return;
  }

  // If product has very few reviews, we already have them all loaded
  if (totalReviews.value <= 10) {
    allReviewsForSizeFit.value = reviews.value;
    return;
  }

  try {
    // Fetch all reviews (fetchAllReviews handles pagination automatically)
    // Note: Consider adding a max limit if products can have thousands of reviews
    allReviewsForSizeFit.value = await fetchAllReviews(props.product.sku);
  } catch (error) {
    console.error('Failed to load all reviews for size fit:', error);
  }
};

const loadMoreReviews = async () => {
  // Store the current number of reviews before loading more
  const previousReviewCount = reviews.value.length;
  
  currentPage.value++;
  await loadReviews();
  
  // Wait for DOM to update, then scroll to the first newly loaded review
  await nextTick();
  
  // Find all review items and scroll to the first new one
  const reviewElements = document.querySelectorAll('[data-review-item]');
  if (reviewElements.length > previousReviewCount) {
    const firstNewReview = reviewElements[previousReviewCount];
    if (firstNewReview) {
      firstNewReview.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Add a small offset to account for any fixed headers
      window.scrollBy({ top: -20, behavior: 'smooth' });
    }
  }
};

const onFilterChange = async (star: number | null) => {
  // Format the filter for the API: { star: 5 } or empty object for all
  currentFilter.value = star ? { star } : {};
  await loadReviews(true);
};

const onReviewSubmitted = async () => {
  // Hide the form after 2 seconds to show success message first
  setTimeout(() => {
    showReviewForm.value = false;
  }, 2000);
  
  // Reload reviews data
  await Promise.all([
    loadReviews(true),
    loadBottomline(),
    loadAllReviewsForSizeFit(), // Reload all reviews to update size fit percentages
  ]);
};

const onQuestionsLoaded = (count: number) => {
  totalQuestions.value = count;
};

</script>

<style lang="postcss" scoped>
/* Custom styling for the review side menu */
</style>
